
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSubscriptionStatus } from '@/utils/subscriptionService';
import { 
  LayoutDashboard, 
  FileText, 
  DollarSign, 
  CreditCard, 
  Settings, 
  Building,
  BookOpen,
  LogOut,
  UserCircle
} from 'lucide-react';
import AdminNavBar from './AdminNavBar';
import { Button } from '@/components/ui/button';

interface AdminLayoutProps {
  children: React.ReactNode;
  isLoading: boolean;
  onLogout: () => void;
  userRole?: string;
}

const AdminLayoutWithSubscription: React.FC<AdminLayoutProps> = ({
  children,
  isLoading,
  onLogout,
  userRole,
}) => {
  const navigate = useNavigate();
  const [subscriptionStatus, setSubscriptionStatus] = useState({
    status: 'loading' as 'loading' | 'active' | 'trial' | 'expired' | 'none' | 'inactive',
    daysRemaining: 0,
    plan: '',
    features: null as {
      maxReferrals: number;
      maxReferralLinks: number;
      maxManagers: number;
    } | null
  });

  useEffect(() => {
    const loadSubscriptionStatus = async () => {
      try {
        const status = await getSubscriptionStatus();
        setSubscriptionStatus({
          status: status.status,
          daysRemaining: status.daysRemaining || 0,
          plan: status.plan || '',
          features: status.features || null
        });
      } catch (error) {
        console.error('Error loading subscription status:', error);
      }
    };

    loadSubscriptionStatus();
  }, []);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  let navItems = [
    { title: "Dashboard", url: "/admin-dashboard", icon: LayoutDashboard },
    { title: "Reports", url: "/admin/reports", icon: FileText },
    { title: "Referrer Payments", url: "/admin/referrer-payments", icon: DollarSign },
    { title: "Payments", url: "/admin/payments", icon: CreditCard },
    { title: "Business Setup", url: "/admin/business-setup", icon: Building },
    { title: "Knowledge Base", url: "/admin/knowledge-base", icon: BookOpen },
    { title: "Profile", url: "/admin/profile", icon: UserCircle },
    { title: "Settings", url: "/admin/settings", icon: Settings },
  ];

  if (userRole === 'superadmin') {
    navItems = [
      { title: "Dashboard", url: "/super-admin/dashboard", icon: LayoutDashboard },
      { title: "Instances", url: "/super-admin/instances", icon: Building },
      { title: "Subscriptions", url: "/super-admin/subscriptions", icon: DollarSign },
      { title: "Plans", url: "/super-admin/plans", icon: FileText },
      { title: "Knowledge Base", url: "/super-admin/knowledge-base", icon: BookOpen },
      { title: "Profile", url: "/super-admin/profile", icon: UserCircle },
      { title: "Settings", url: "/super-admin/settings", icon: Settings },
    ];
  }

  return (
    <div className="h-screen w-full bg-gray-100">
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="bg-primary-blue/10 p-4 flex justify-between items-center border-b">
          <div className="flex items-center">
            <h2 className="text-lg font-semibold">Admin Dashboard</h2>
            {userRole && (
              <span className="ml-3 px-3 py-1 text-xs font-semibold text-white rounded-full bg-primary-blue">
                {userRole.charAt(0).toUpperCase() + userRole.slice(1)}
              </span>
            )}
          </div>
          
          <Button 
            variant="destructive" 
            size="sm"
            onClick={onLogout}
            className="flex items-center gap-2"
          >
            <LogOut className="h-4 w-4" />
            Logout
          </Button>
        </div>
        
        <AdminNavBar navItems={navItems} onLogout={onLogout} />
        
        <main className="flex-1 overflow-y-auto px-6 py-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayoutWithSubscription;
